import React, { useState } from 'react';
import { X, DollarSign, Loader2, CreditCard } from 'lucide-react';

const PRESET_AMOUNTS = [
  { cents: 500, label: '$5' },
  { cents: 1000, label: '$10' },
  { cents: 2000, label: '$20' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
];

const TopUpModal = ({
  isOpen,
  onClose,
  paymentMethods = [],
  onTopUp,
  loading = false,
}) => {
  const [selectedAmount, setSelectedAmount] = useState(2000); // Default $20
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethods.find((pm) => pm.isDefault)?.id || paymentMethods[0]?.id || ''
  );
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handlePresetClick = (cents) => {
    setSelectedAmount(cents);
    setUseCustom(false);
    setCustomAmount('');
  };

  const handleCustomChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
    setUseCustom(true);
    if (value) {
      setSelectedAmount(Math.round(parseFloat(value) * 100) || 0);
    }
  };

  const finalAmount = useCustom ? Math.round(parseFloat(customAmount) * 100) || 0 : selectedAmount;

  const handleSubmit = async () => {
    if (finalAmount < 500) {
      setError('Minimum top-up amount is $5.00');
      return;
    }
    if (finalAmount > 50000) {
      setError('Maximum top-up amount is $500.00');
      return;
    }
    if (!selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setError(null);
    try {
      await onTopUp(finalAmount, selectedPaymentMethod);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to process payment');
    }
  };

  const formatCents = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-freshroad-black flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-freshroad-teal" />
            Add Funds
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Preset amounts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Amount
            </label>
            <div className="grid grid-cols-5 gap-2">
              {PRESET_AMOUNTS.map(({ cents, label }) => (
                <button
                  key={cents}
                  onClick={() => handlePresetClick(cents)}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    !useCustom && selectedAmount === cents
                      ? 'border-freshroad-teal bg-freshroad-teal text-white'
                      : 'border-gray-200 hover:border-freshroad-teal text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or Enter Custom Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="text"
                value={customAmount}
                onChange={handleCustomChange}
                placeholder="0.00"
                className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-freshroad-teal focus:border-transparent ${
                  useCustom ? 'border-freshroad-teal' : 'border-gray-200'
                }`}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Min $5.00, Max $500.00</p>
          </div>

          {/* Payment method selection */}
          {paymentMethods.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-freshroad-teal focus:border-transparent"
              >
                {paymentMethods.map((pm) => (
                  <option key={pm.id} value={pm.id}>
                    {pm.brand?.toUpperCase()} •••• {pm.last4} {pm.isDefault ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {paymentMethods.length === 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
              Please add a payment method first.
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Amount to add</span>
              <span className="text-2xl font-bold text-freshroad-black">
                {formatCents(finalAmount)}
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading || paymentMethods.length === 0 || finalAmount < 500}
            className="w-full flex items-center justify-center gap-2 py-3 bg-freshroad-teal text-white rounded-lg hover:bg-freshroad-teal-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Add {formatCents(finalAmount)}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
